import { twMerge } from 'tailwind-merge'

type CssEntry = { $$css: true } & Record<string, unknown>

const isCssEntry = (entry: object): entry is CssEntry =>
    (entry as { $$css?: unknown }).$$css === true

/**
 * On web, nativewind's cssInterop deletes an icon's `className` and re-emits it as a
 * react-native-web style entry (`{ $$css: true, [classes]: classes }`). react-native-svg glyphs
 * read that back as classes; a plain DOM `<svg>` — tenant and SVGR glyphs — drops it, and with
 * it the tint and the shrink guard (AOS-1396). This returns the classes and the remaining
 * inline style separately so a DOM glyph can take both, keeping any className the caller
 * still passed alongside.
 */
export const splitWebStyle = (
    style: unknown,
    className?: unknown,
): { className?: string; style?: object } => {
    const classes: string[] = typeof className === 'string' && className ? [className] : []
    const inline: Record<string, unknown> = {}
    const collect = (entry: unknown) => {
        if (Array.isArray(entry)) {
            entry.forEach(collect)
            return
        }
        if (!entry || typeof entry !== 'object') {
            return
        }
        if (isCssEntry(entry)) {
            for (const [key, value] of Object.entries(entry)) {
                if (key !== '$$css' && typeof value === 'string') {
                    classes.push(value)
                }
            }
            return
        }
        Object.assign(inline, entry)
    }
    collect(style)
    return {
        ...(classes.length ? { className: twMerge(...classes) } : {}),
        ...(Object.keys(inline).length ? { style: inline } : {}),
    }
}
